/* ═══════════════════════════════════════════════════════════════
   CellEditor — modal popover for adding / editing a single
   (member × project × day) forecast cell. Read-only for vacation
   rows + insufficient permissions.

   Phase is NOT user-selectable here. Per project_phase_is_project_level
   memory: the PM / billing manager sets `projects.current_phase_id`
   once; the server-side trigger stamps the current phase onto every
   forecast / hour-entry write. The picker would be redundant.

   Project options are split into "Your projects" (the member's assigned
   set — derived from existing forecasts + PM/PIC) and "All firm projects"
   when the editor is operating on the current user's row. When an admin
   is editing someone else's row, all firm projects are shown ungrouped.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import type { TeamMember, HourForecast, Project } from '../../../shared/types';
import { TimeService } from '../../services/timeService';
import { SearchableSelect, type SelectOption } from '../../components/SearchableSelect';
import {
  Modal,
  btnDanger,
  btnGhost,
  btnPrimary,
  fieldBlock,
  fieldLabel,
  formatLong,
  textInput,
} from './_shared';

export function CellEditor({
  member,
  dayDate,
  forecastId,
  existing,
  projects,
  allProjects,
  assignedProjectIds,
  canEdit,
  onClose,
  onSaved,
}: {
  member: TeamMember;
  dayDate: string;
  forecastId?: string;
  existing: HourForecast[];
  /** Filter-narrowed projects (toolbar filter) — NOT used for the editor list. */
  projects: Project[];
  /** Full firm project list — fallback / search corpus. */
  allProjects: Project[];
  /** Project IDs this `member` is currently assigned on (derived from
   *  forecasts/entries/PM/PIC). Pinned to the top of the picker so the
   *  default action is fast; any firm project is still searchable below. */
  assignedProjectIds: Set<string>;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = forecastId ? existing.find((f) => f.id === forecastId) : null;
  const isVacation = editing?.sourceKind === 'vacation';
  const readOnly = !canEdit || isVacation;
  // Default hours: existing row wins; otherwise 8h for full-time, 4h for
  // part-time, weeklyCapacityHours/5 for custom contracts.
  const defaultHours =
    editing?.hours ??
    (member.weeklyCapacityHours
      ? Math.max(1, Math.round((member.weeklyCapacityHours / 5) * 2) / 2)
      : member.employmentType === 'part_time_hourly'
        ? 4
        : 8);
  const [projectId, setProjectId] = useState<string>(editing?.projectId || '');
  const [hours, setHours] = useState<number>(defaultHours);
  const [saving, setSaving] = useState(false);

  // Project picker source = full firm list (editor is the place where you
  // can ALWAYS reach any project, regardless of page-level filters).
  const list = allProjects.length ? allProjects : projects;

  // Build option groups: assigned first, then "All firm projects". When the
  // member has no assignments, fall back to a single ungrouped list.
  const projectOptions: SelectOption[] = (() => {
    const assigned: SelectOption[] = [];
    const others: SelectOption[] = [];
    for (const p of list) {
      const opt: SelectOption = {
        value: p.id,
        label: p.name,
        hint: p.projectNumber || undefined,
      };
      if (assignedProjectIds.has(p.id)) {
        assigned.push({ ...opt, group: 'Your projects' });
      } else {
        others.push({ ...opt, group: 'All firm projects' });
      }
    }
    if (assigned.length === 0) {
      // No assignments yet — show a flat list (no group headers) so we
      // don't dangle an empty "Your projects" section.
      return others.map((o) => ({ value: o.value, label: o.label, hint: o.hint }));
    }
    return [...assigned, ...others];
  })();

  async function save() {
    if (readOnly || !projectId || hours <= 0) return;
    setSaving(true);
    try {
      // phaseId intentionally NOT sent — the server-side trigger stamps it
      // from projects.current_phase_id. See migration 017.
      await TimeService.setForecastDayCell({
        teamMemberId: member.id,
        projectId,
        dayDate,
        hours,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!editing || readOnly) return;
    setSaving(true);
    try {
      await TimeService.deleteForecast(editing.id);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title={editing ? 'Edit forecast' : 'Add forecast'}>
      <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 12 }}>
        {member.name} — {formatLong(dayDate)}
      </div>

      {/* Diagnostic banners — vacation, read-only, no-projects. No silent
       *  no-ops; user always knows WHY they can't save. */}
      {readOnly && (
        <div
          style={{
            padding: '10px 12px',
            marginBottom: 12,
            background: isVacation
              ? 'color-mix(in srgb, #6b7280 15%, transparent)'
              : 'color-mix(in srgb, var(--dg) 12%, transparent)',
            border: `1px solid ${isVacation ? '#6b7280' : 'var(--dg)'}`,
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--tx)',
            lineHeight: 1.4,
          }}
        >
          {isVacation ? (
            <>
              🏖 <strong>Vacation forecast</strong> — this cell was auto-created from an approved
              vacation request. Edit the underlying vacation request to change it.
            </>
          ) : (
            <>
              🔒 <strong>Read-only</strong> — you don't have permission to edit this person's
              forecast. Sign out and back in if you just got new permissions, or ask an admin for{' '}
              <code style={{ fontSize: 11 }}>forecasting.edit_team</code> /{' '}
              <code style={{ fontSize: 11 }}>forecasting.edit_any</code>.
            </>
          )}
        </div>
      )}

      {list.length === 0 && !readOnly && (
        <div
          style={{
            padding: '10px 12px',
            marginBottom: 12,
            background: 'color-mix(in srgb, var(--ac) 10%, transparent)',
            border: '1px solid var(--ac)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--tx)',
          }}
        >
          ⚠ Your firm has no projects yet. Create one in the Projects tab, then come back here to
          forecast hours against it.
        </div>
      )}

      <label style={fieldBlock}>
        <span style={fieldLabel}>Project</span>
        <SearchableSelect
          value={projectId}
          options={projectOptions}
          onChange={setProjectId}
          placeholder={
            assignedProjectIds.size > 0 ? '— Pick from your projects —' : '— Pick a project —'
          }
          searchPlaceholder="Search projects…"
          disabled={!!editing || readOnly}
          clearable={false}
        />
      </label>

      <label style={fieldBlock}>
        <span style={fieldLabel}>Hours</span>
        <input
          type="number"
          min="0"
          step="0.5"
          value={hours}
          onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
          disabled={readOnly}
          style={{ ...textInput, width: 100, fontFamily: "'JetBrains Mono', monospace" }}
          autoFocus={!readOnly}
          aria-describedby="hours-default-hint"
        />
        {!editing && (
          <span id="hours-default-hint" style={{ fontSize: 10, color: 'var(--tm)', marginLeft: 8 }}>
            Default {defaultHours}h ({member.employmentType === 'part_time_hourly' ? 'PT' : 'FT'})
          </span>
        )}
      </label>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'space-between' }}>
        {editing && !readOnly ? (
          <button onClick={remove} style={btnDanger} disabled={saving}>
            Delete
          </button>
        ) : (
          <span />
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={btnGhost} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button onClick={save} style={btnPrimary} disabled={saving || !projectId || hours <= 0}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
